import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lightning, ArrowSquareOut, FloppyDisk } from "@phosphor-icons/react";

// External-service integration settings (currently only the Flux ticket URL
// template used by the ODT list badges).
export default function SettingsIntegrations() {
  const [fluxUrl, setFluxUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations");
      setFluxUrl(data?.flux_ticket_url_template || "");
      setSaved(data?.flux_ticket_url_template || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (fluxUrl && !fluxUrl.includes("{ticket}")) {
      toast.error("L'URL deve contenere il segnaposto {ticket}");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/integrations", { flux_ticket_url_template: fluxUrl });
      setFluxUrl(data.flux_ticket_url_template);
      setSaved(data.flux_ticket_url_template);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const preview = fluxUrl.replace("{ticket}", "TK-0438");
  const dirty = fluxUrl !== saved;

  if (loading) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <section className="border border-neutral-200 bg-white p-5 md:p-6" data-testid="settings-integrations">
      <div className="flex items-start gap-3">
        <Lightning size={22} weight="fill" className="text-[#002FA7] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="label-eyebrow text-[#002FA7] mb-1">Flux · Ticketing</div>
          <h3 className="font-display font-bold text-lg tracking-tight">URL apertura ticket</h3>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            Modello di URL usato per rendere cliccabili i codici <span className="font-mono">TK-XXXX</span> nella
            lista Ordini di Trasferimento. Il segnaposto <span className="font-mono">{"{ticket}"}</span> verrà
            sostituito con il codice specifico.
          </p>

          <label className="block mt-4">
            <span className="label-eyebrow block mb-1">URL Template</span>
            <input
              data-testid="flux-url-input"
              type="text"
              value={fluxUrl}
              onChange={(e) => setFluxUrl(e.target.value)}
              placeholder="https://flux.example.com/tickets/{ticket}"
              className="w-full px-3 py-2 border border-neutral-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </label>

          {fluxUrl && fluxUrl.includes("{ticket}") && (
            <div className="mt-3 text-xs text-neutral-600">
              Anteprima con <span className="font-mono">TK-0438</span>:{" "}
              <a
                href={preview}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="flux-url-preview"
                className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-mono break-all"
              >
                {preview}
                <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving || !dirty}
              data-testid="flux-url-save"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? "Salvo…" : "Salva"}
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => setFluxUrl(saved)}
                className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100"
              >Annulla</button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
